import { useEffect, useState } from "react";
import { Star, Quote } from "lucide-react";
import { getTestimonials } from "@/lib/content";
import { Reveal } from "@/components/site/motion";
import { motion } from "framer-motion";

const FALLBACK = [
  { id: "t1", name: "Priya S.", area: "Koramangala", rating: 5,
    body: "Got my 3 BHK repainted in 4 days. Team was punctual and left the house cleaner than they found it. Final price matched the quote exactly.",
    img: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=160&h=160&fit=crop", order: 1 },
  { id: "t2", name: "Rahul M.", area: "HSR Layout", rating: 5,
    body: "Exterior repaint survived two Bangalore monsoons with zero peeling. The foreman walked me through every coat.",
    img: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=160&h=160&fit=crop", order: 2 },
  { id: "t3", name: "Aisha K.", area: "Indiranagar", rating: 5,
    body: "The accent wall in our living room gets compliments every time guests visit. Loved how patient the designer was with colour choices.",
    img: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=160&h=160&fit=crop", order: 3 },
  { id: "t4", name: "Vikram R.", area: "Whitefield", rating: 4,
    body: "Scheduled a 2 BHK repaint around my WFH schedule — they worked in two zones. Happy with the finish and minimal dust.",
    img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=160&h=160&fit=crop", order: 4 },
];

export default function Testimonials() {
  const [items, setItems] = useState(FALLBACK);

  useEffect(() => {
    getTestimonials()
      .then((d) => d?.length && setItems(d))
      .catch(() => {});
  }, []);

  return (
    <section data-testid="testimonials-section" className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <Reveal className="text-xs font-bold tracking-[0.22em] uppercase text-orange-600">
              Happy customers
            </Reveal>
            <Reveal as="h2" delay={0.05} className="mt-3 font-display font-black text-3xl sm:text-4xl lg:text-5xl text-ink">
              Rated 4.9 by 480+ Bangalore homes.
            </Reveal>
          </div>
          <Reveal delay={0.1} className="flex items-center gap-2">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-5 w-5 fill-orange-500 text-orange-500" />
              ))}
            </div>
            <div className="text-sm font-bold text-ink">4.9 / 5</div>
          </Reveal>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((r, i) => (
            <motion.article
              key={r.id || r.name}
              data-testid={`testimonial-card-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -4 }}
              className="rounded-2xl bg-white hairline shadow-soft p-6 flex flex-col transition-shadow duration-300"
            >
              <Quote className="h-6 w-6 text-orange-400" />
              <p className="mt-3 text-[15px] text-ink-2 leading-relaxed flex-1">“{r.body}”</p>
              <div className="mt-5 pt-5 border-t border-slate-100 flex items-center gap-3">
                {r.img ? (
                  <img src={r.img} alt={r.name} className="h-10 w-10 rounded-full object-cover" />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-orange-100 grid place-items-center font-display font-bold text-orange-700">
                    {r.name?.charAt(0) || "?"}
                  </div>
                )}
                <div>
                  <div className="font-bold text-ink text-sm">{r.name}</div>
                  {r.area ? <div className="text-xs text-muted-ink">{r.area}</div> : null}
                </div>
                <div className="ml-auto flex">
                  {[...Array(r.rating || 5)].map((_, i) => (
                    <Star key={i} className="h-3.5 w-3.5 fill-orange-500 text-orange-500" />
                  ))}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
